import type {
  CompleteMagicLinkResponse,
  Principal,
} from "@tactical-agent-neural-knowledge/sdk/contracts/auth";
import type { Workspace } from "@tactical-agent-neural-knowledge/sdk/contracts/workspace";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AUTH_MODE } from "../../lib/env";
import { apisForUser, authApi, workspaceApi } from "../../lib/tank";
import { tokens } from "../../lib/tokens";

export type SessionStatus = "loading" | "anon" | "authed";

/**
 * One account signed in on this device, with the workspaces it belongs to.
 *
 * The same person routinely holds workspaces under different email addresses, which are different
 * accounts. The device keeps them all signed in at once so moving between them is a click, not a
 * magic link, and so unread counts stay live in every one of them.
 */
export interface Account {
  principal: Principal;
  workspaces: Workspace[];
}

export interface Session {
  status: SessionStatus;
  me: Principal | undefined;
  /** Every account signed in on this device, oldest first. */
  accounts: Account[];
  /** The active account's workspaces. Use `accounts` to reach the others. */
  workspaces: Workspace[];
  /** Re-run GetMe + ListWorkspaces. */
  reload: () => Promise<void>;
  /** Adopt the credentials CompleteMagicLink returned. */
  signIn: (res: CompleteMagicLinkResponse) => Promise<void>;
  signOut: () => Promise<void>;
  /** Drop one account, leaving the rest signed in. */
  signOutAccount: (userId: string) => Promise<void>;
  /** A workspace just created, under ownerId (the active account when omitted). */
  addWorkspace: (ws: Workspace, ownerId?: string) => void;
}

const SessionContext = createContext<Session | null>(null);

const NEXT_KEY = "tank.next";

/** Remember where to go after sign-in (survives the round trip through email). */
export const postLoginTarget = {
  set(path: string): void {
    try {
      sessionStorage.setItem(NEXT_KEY, path);
    } catch {
      // ignore
    }
  },
  take(): string | undefined {
    try {
      const v = sessionStorage.getItem(NEXT_KEY) ?? undefined;
      sessionStorage.removeItem(NEXT_KEY);
      return v?.startsWith("/") ? v : undefined;
    } catch {
      return undefined;
    }
  },
};

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [me, setMe] = useState<Principal | undefined>();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);

  const reload = useCallback(async () => {
    if (AUTH_MODE === "bearer" && !tokens.refresh() && !tokens.access()) {
      setStatus("anon");
      setMe(undefined);
      setWorkspaces([]);
      return;
    }
    try {
      const [meRes, wsRes] = await Promise.all([authApi.getMe({}), workspaceApi.listWorkspaces({})]);
      setMe(meRes.me);
      setWorkspaces(wsRes.workspaces);
      setStatus(meRes.me ? "authed" : "anon");
      if (meRes.me) void loadAccounts(meRes.me, wsRes.workspaces).then(setAccounts);
    } catch {
      setMe(undefined);
      setWorkspaces([]);
      setStatus("anon");
    }
  }, []);

  const signIn = useCallback(async (res: CompleteMagicLinkResponse) => {
    if (AUTH_MODE === "bearer") {
      tokens.set({
        accessToken: res.accessToken,
        refreshToken: res.refreshToken,
        accessExpiresAtMs: res.accessExpiresAt ? Number(res.accessExpiresAt.seconds) * 1000 : undefined,
      });
    }
    setMe(res.me);
    setStatus(res.me ? "authed" : "anon");
    const wsRes = await workspaceApi.listWorkspaces({});
    setWorkspaces(wsRes.workspaces);
    if (res.me) setAccounts(await loadAccounts(res.me, wsRes.workspaces));
  }, []);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout({});
    } catch {
      // the local session is gone either way
    }
    tokens.clear();
    setMe(undefined);
    setWorkspaces([]);
    setAccounts([]);
    setStatus("anon");
  }, []);

  // Signing one account out must leave the others usable, so this is never a full logout unless it
  // was the last one; the server says which happened.
  const signOutAccount = useCallback(
    async (userId: string) => {
      const res = await authApi.signOutIdentity({ userId });
      if (!res.sessionRemains) {
        tokens.clear();
        setMe(undefined);
        setWorkspaces([]);
        setAccounts([]);
        setStatus("anon");
        return;
      }
      await reload();
    },
    [reload],
  );

  const addWorkspace = useCallback(
    (ws: Workspace, ownerId?: string) => {
      const owner = ownerId ?? me?.id;
      if (!ownerId || ownerId === me?.id) {
        setWorkspaces((list) => (list.some((w) => w.id === ws.id) ? list : [...list, ws]));
      }
      // The switcher draws from the per-account lists, not the active list: a workspace added
      // only to the latter was created and then nowhere to be found.
      setAccounts((list) =>
        list.map((a) =>
          a.principal.id === owner && !a.workspaces.some((w) => w.id === ws.id)
            ? { ...a, workspaces: [...a.workspaces, ws] }
            : a,
        ),
      );
    },
    [me?.id],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  const value = useMemo<Session>(
    () => ({ status, me, accounts, workspaces, reload, signIn, signOut, signOutAccount, addWorkspace }),
    [status, me, accounts, workspaces, reload, signIn, signOut, signOutAccount, addWorkspace],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Every account this device is signed in as, with its workspaces.
 *
 * ListIdentities is additive on the server, so an older api that does not know about it leaves the
 * device with exactly the account it is already using rather than an empty rail.
 */
async function loadAccounts(me: Principal, mine: Workspace[]): Promise<Account[]> {
  let identities: Principal[];
  try {
    const res = await authApi.listIdentities({});
    identities = res.identities.flatMap((i) => (i.principal ? [i.principal] : []));
  } catch {
    return [{ principal: me, workspaces: mine }];
  }
  if (identities.length <= 1) return [{ principal: me, workspaces: mine }];
  const loaded = await Promise.all(
    identities.map(async (principal) => {
      if (principal.id === me.id) return { principal, workspaces: mine };
      try {
        const res = await apisForUser(principal.id).workspaces.listWorkspaces({});
        return { principal, workspaces: res.workspaces };
      } catch {
        // One unreachable account must not blank the rail for the others.
        return { principal, workspaces: [] };
      }
    }),
  );
  return loaded;
}

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}

/** Every workspace on the device, across all its accounts. */
export function allWorkspaces(accounts: Account[], fallback: Workspace[]): Workspace[] {
  const all = accounts.flatMap((a) => a.workspaces);
  return all.length > 0 ? all : fallback;
}

/** Resolve a `/w/$ws` param (slug or id) against the signed-in user's workspaces. */
export function findWorkspace(workspaces: Workspace[], key: string): Workspace | undefined {
  return workspaces.find((w) => w.slug === key) ?? workspaces.find((w) => w.id === key);
}

/**
 * Which account owns a `/w/$ws` route, and the workspace it names.
 *
 * A URL identifies a workspace, not an account, so opening a link for a workspace held under the
 * device's other address must switch accounts rather than say "workspace not found".
 */
export function findAccountWorkspace(
  accounts: Account[],
  key: string,
): { account: Account; workspace: Workspace } | undefined {
  for (const account of accounts) {
    const workspace = findWorkspace(account.workspaces, key);
    if (workspace) return { account, workspace };
  }
  return undefined;
}
